import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { X, Merge, Search } from "lucide-react";
import { deriveInitials } from "@/lib/pipeline";
import { stageColor } from "@/lib/palette";

export default function MergeCardDialog({ primary, onClose, onMerged }) {
  const [cards, setCards] = useState([]);
  const [q, setQ] = useState("");
  const [secondaryId, setSecondaryId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cards", { params: { status: "active" } }).then(({ data }) =>
      setCards(data.filter((c) => c.id !== primary.id))
    );
  }, [primary.id]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cards.slice(0, 50);
    return cards
      .filter((c) =>
        `${c.full_name} ${c.av_id} ${c.payer_mco}`.toLowerCase().includes(s)
      )
      .slice(0, 50);
  }, [cards, q]);

  const submit = async () => {
    if (!secondaryId) return toast.error("Pick a secondary card to merge");
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${primary.id}/merge`, {
        secondary_id: secondaryId,
      });
      toast.success("Merge complete — secondary archived");
      onMerged?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sec = cards.find((c) => c.id === secondaryId);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="merge-dialog"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#15233B]/10 bg-[#F6F3EC] flex items-center gap-3">
          <Merge className="h-4 w-4 text-[#FBBF24]" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#15233B]/60">
              Merge into primary
            </div>
            <h3 className="text-lg font-extrabold tracking-tight text-[#15233B]">
              {primary.full_name}{" "}
              <span className="text-xs font-mono text-[#15233B]/60">{primary.av_id}</span>
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="merge-close"
            className="p-1.5 rounded hover:bg-white"
          >
            <X className="h-4 w-4 text-[#15233B]/60" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="text-xs text-[#15233B]/70">
            Pick a duplicate card to merge into <b>{primary.full_name}</b>. Ad-hoc tasks and
            the full activity log will be transferred, and the duplicate will be archived
            with reason “Merged into {primary.av_id}”.
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#15233B]/40" />
            <input
              data-testid="merge-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / AV-ID…"
              autoFocus
              className="w-full pl-9 pr-3 py-2 border border-[#15233B]/15 rounded text-sm bg-white"
            />
          </div>
          <div className="max-h-72 overflow-y-auto border border-[#15233B]/10 rounded">
            {visible.length === 0 && (
              <div className="px-3 py-6 text-xs italic text-[#15233B]/40 text-center">
                No active cards match.
              </div>
            )}
            {visible.map((c) => {
              const sc = stageColor(c.current_stage);
              const isSel = secondaryId === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setSecondaryId(c.id)}
                  data-testid={`merge-pick-${c.av_id}`}
                  className={`w-full px-3 py-2 flex items-center gap-3 text-left border-b border-[#15233B]/8 last:border-b-0 ${
                    isSel ? "bg-[#FBBF24]/10" : "hover:bg-[#F6F3EC]/60"
                  }`}
                >
                  <div
                    className="h-8 w-8 shrink-0 rounded-full grid place-items-center text-[10px] font-bold"
                    style={{ backgroundColor: sc.primary, color: sc.text }}
                  >
                    {deriveInitials(c.full_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-[#15233B] truncate">
                      {c.full_name}
                    </div>
                    <div className="text-[11px] text-[#15233B]/60 truncate">
                      <span className="font-mono">{c.av_id}</span> · {c.payer_mco} · Stage{" "}
                      {c.current_stage}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#15233B]/10 flex items-center gap-2">
          {sec && (
            <div className="text-xs text-[#15233B]/70">
              Will merge <b>{sec.full_name}</b> ({sec.av_id}) →{" "}
              <b>{primary.full_name}</b>
            </div>
          )}
          <div className="ml-auto flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!secondaryId || busy}
              data-testid="merge-submit"
              className="px-4 py-2 rounded bg-[#FBBF24] hover:bg-[#F59E0B] text-white text-sm font-bold disabled:opacity-40"
            >
              {busy ? "Merging…" : "Merge"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
